import {
  PLATFORM_ADMIN_ROLES,
  PLATFORM_PERMISSIONS,
  type PlatformAdminRole,
  type PlatformPermission,
} from "@ridereserv/shared/constants";
import { hasPlatformPermission } from "@ridereserv/shared/utils";
import { matchPath } from "react-router-dom";
import type { PlatformAdmin } from "@/lib/api";

/** A permission requirement attached to one protected admin route. */
export interface AdminRoutePolicy {
  /** React Router path pattern. Dynamic segments are declared explicitly. */
  pattern: string;
  /** Every listed permission is required. */
  requiredPermissions: readonly PlatformPermission[];
  /** Concrete destination to offer when this is the first accessible route. */
  entryPath?: string;
}

const VIEW_ALL_DATA = [PLATFORM_PERMISSIONS.VIEW_ALL_DATA] as const;
const KNOWN_PLATFORM_ADMIN_ROLES = new Set<string>(Object.values(PLATFORM_ADMIN_ROLES));
const KNOWN_PLATFORM_PERMISSIONS = new Set<string>(Object.values(PLATFORM_PERMISSIONS));
const UNKNOWN_PLATFORM_ADMIN_ROLE = "" as PlatformAdminRole;

/**
 * Complete permission policy for the protected routes declared in `App.tsx`.
 *
 * Order is intentional: it is also the preference order used when a denied
 * route needs to offer the admin a concrete page they can open. Dynamic detail
 * routes have no `entryPath` because they require an entity identifier.
 */
export const ADMIN_ROUTE_POLICIES = [
  { pattern: "/", requiredPermissions: VIEW_ALL_DATA, entryPath: "/" },
  {
    pattern: "/driver-requests",
    requiredPermissions: VIEW_ALL_DATA,
    entryPath: "/driver-requests",
  },
  {
    pattern: "/company-requests",
    requiredPermissions: VIEW_ALL_DATA,
    entryPath: "/company-requests",
  },
  { pattern: "/inquiries", requiredPermissions: VIEW_ALL_DATA, entryPath: "/inquiries" },
  { pattern: "/users", requiredPermissions: VIEW_ALL_DATA, entryPath: "/users" },
  { pattern: "/users/:id", requiredPermissions: VIEW_ALL_DATA },
  { pattern: "/drivers", requiredPermissions: VIEW_ALL_DATA, entryPath: "/drivers" },
  { pattern: "/drivers/:id", requiredPermissions: VIEW_ALL_DATA },
  { pattern: "/companies", requiredPermissions: VIEW_ALL_DATA, entryPath: "/companies" },
  { pattern: "/companies/:id", requiredPermissions: VIEW_ALL_DATA },
  { pattern: "/vehicles", requiredPermissions: VIEW_ALL_DATA, entryPath: "/vehicles" },
  {
    pattern: "/reservations",
    requiredPermissions: VIEW_ALL_DATA,
    entryPath: "/reservations",
  },
  { pattern: "/reservations/:id", requiredPermissions: VIEW_ALL_DATA },
  {
    pattern: "/messaging",
    requiredPermissions: [PLATFORM_PERMISSIONS.VIEW_ALL_DATA, PLATFORM_PERMISSIONS.EDIT_USERS],
    entryPath: "/messaging",
  },
  { pattern: "/support", requiredPermissions: VIEW_ALL_DATA, entryPath: "/support" },
  { pattern: "/tiers", requiredPermissions: VIEW_ALL_DATA, entryPath: "/tiers" },
  {
    pattern: "/audit-log",
    requiredPermissions: [PLATFORM_PERMISSIONS.ACCESS_LOGS],
    entryPath: "/audit-log",
  },
  {
    pattern: "/sms-consent",
    requiredPermissions: VIEW_ALL_DATA,
    entryPath: "/sms-consent",
  },
  {
    pattern: "/complimentary-grants",
    requiredPermissions: VIEW_ALL_DATA,
    entryPath: "/complimentary-grants",
  },
  { pattern: "/admins", requiredPermissions: VIEW_ALL_DATA, entryPath: "/admins" },
  {
    pattern: "/test-accounts",
    requiredPermissions: [PLATFORM_PERMISSIONS.EDIT_USERS],
    entryPath: "/test-accounts",
  },
] as const satisfies readonly AdminRoutePolicy[];

const ADMIN_URL_ORIGIN = "https://admin.ridereserv.invalid";

/**
 * Reduce a local route target to the pathname React Router matches.
 * Absolute, protocol-relative, malformed, and non-root-relative targets fail
 * closed so notification/deep-link consumers can safely reuse this policy.
 * @param target - Root-relative route target, optionally with query/hash.
 * @returns The local pathname, or null when the target is not safely local.
 */
function localPathname(target: string): string | null {
  if (!target.startsWith("/") || target.startsWith("//")) return null;

  try {
    const parsed = new URL(target, ADMIN_URL_ORIGIN);
    return parsed.origin === ADMIN_URL_ORIGIN ? parsed.pathname : null;
  } catch {
    return null;
  }
}

/**
 * Convert the API's wire-shaped admin to the canonical shared helper input.
 * Persisted browser state is untrusted at runtime, so malformed values are
 * reduced to an unknown role and an empty/canonical permission list before the
 * shared helper sees them. A valid explicit grant still supplements an unknown
 * role, matching the server helper's established behavior.
 * @param admin - Platform-admin session received from the API.
 * @returns The canonical shape consumed by the shared permission helper.
 */
function canonicalPermissionSubject(admin: PlatformAdmin): {
  role: PlatformAdminRole;
  permissions: PlatformPermission[];
} {
  const role: unknown = admin.role;
  const permissions: unknown = admin.permissions;

  return {
    role:
      typeof role === "string" && KNOWN_PLATFORM_ADMIN_ROLES.has(role)
        ? (role as PlatformAdminRole)
        : UNKNOWN_PLATFORM_ADMIN_ROLE,
    permissions: Array.isArray(permissions)
      ? permissions.filter(
          (permission): permission is PlatformPermission =>
            typeof permission === "string" && KNOWN_PLATFORM_PERMISSIONS.has(permission)
        )
      : [],
  };
}

/**
 * Return true only when the admin has every required effective permission.
 * @param admin - Current platform admin, or null without a verified session.
 * @param requiredPermissions - Permissions combined with all-of semantics.
 * @returns Whether every permission is granted by role or explicit grant.
 */
export function hasAdminPermissions(
  admin: PlatformAdmin | null,
  requiredPermissions: readonly PlatformPermission[]
): boolean {
  if (!admin || requiredPermissions.length === 0) return false;
  const subject = canonicalPermissionSubject(admin);
  return requiredPermissions.every((permission) => hasPlatformPermission(subject, permission));
}

/**
 * Find the all-of permission requirement for a protected local path.
 * @param target - Root-relative route target, optionally with query/hash.
 * @returns The route requirement, or null for an unknown/non-local target.
 */
export function requiredPermissionsForAdminPath(
  target: string
): readonly PlatformPermission[] | null {
  const pathname = localPathname(target);
  if (!pathname) return null;

  const policy = ADMIN_ROUTE_POLICIES.find(({ pattern }) =>
    matchPath({ path: pattern, end: true }, pathname)
  );
  return policy?.requiredPermissions ?? null;
}

/**
 * Return whether an admin may open a protected local route.
 * @param admin - Current platform admin, or null without a verified session.
 * @param target - Root-relative route target, optionally with query/hash.
 * @returns Whether the route is known and all required permissions are held.
 */
export function canAccessAdminPath(admin: PlatformAdmin | null, target: string): boolean {
  const requirement = requiredPermissionsForAdminPath(target);
  return requirement !== null && hasAdminPermissions(admin, requirement);
}

/**
 * Return the first concrete protected route the admin may open, if any.
 * @param admin - Current platform admin, or null without a verified session.
 * @returns A concrete permitted path, or null when none is available.
 */
export function firstAccessibleAdminPath(admin: PlatformAdmin | null): string | null {
  for (const policy of ADMIN_ROUTE_POLICIES) {
    if ("entryPath" in policy && hasAdminPermissions(admin, policy.requiredPermissions)) {
      return policy.entryPath;
    }
  }
  return null;
}
